import { useState } from 'react';

import type { ApiScope, Member } from '@tasks/contracts';

import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select } from '../../components/Select';
import styles from './SettingsPages.module.css';

/** Права токена в порядке UI: подписи совпадают с docs/12 §2.2. */
export const SCOPE_LABELS: readonly {
  readonly value: ApiScope;
  readonly label: string;
  readonly group: string;
}[] = [
  { value: 'issues:read', label: 'Чтение задач', group: 'Задачи' },
  { value: 'issues:write', label: 'Изменение задач', group: 'Задачи' },
  { value: 'comments:read', label: 'Чтение комментариев', group: 'Комментарии' },
  { value: 'comments:write', label: 'Добавление комментариев', group: 'Комментарии' },
  { value: 'attachments:read', label: 'Скачивание вложений', group: 'Вложения' },
  { value: 'attachments:write', label: 'Загрузка вложений', group: 'Вложения' },
  { value: 'webhooks:read', label: 'Чтение подписок', group: 'Вебхуки' },
  { value: 'webhooks:write', label: 'Управление подписками', group: 'Вебхуки' },
  { value: 'wiki:read', label: 'Чтение wiki', group: 'Wiki' },
  { value: 'wiki:write', label: 'Редактирование wiki', group: 'Wiki' },
];

/** Умолчание формы: минимум прав, который нужен интеграции для чтения. */
export const READONLY_SCOPES: readonly ApiScope[] = [
  'issues:read',
  'comments:read',
  'attachments:read',
];

export interface ApiTokenFormValues {
  readonly name: string;
  readonly scopes: ApiScope[];
  readonly expiresInDays: number | null;
  readonly ownerId: string;
}

export interface ApiTokenFormModalProps {
  readonly open: boolean;
  readonly isPending: boolean;
  readonly members: readonly Member[];
  readonly defaultOwnerId: string;
  readonly onSubmit: (values: ApiTokenFormValues) => void;
  readonly onClose: () => void;
}

/**
 * Выпуск токена публичного API (docs/12 §Приложение A.1).
 *
 * Права выбираются осознанно: `webhooks:write` — это право на выгрузку данных
 * проекта на произвольный URL, а подписка настраивается **только** публичным
 * API `/api/v1/webhooks` (docs/12 §9), поэтому расширять набор «на всякий
 * случай» не нужно.
 */
export function ApiTokenFormModal({
  open,
  isPending,
  members,
  defaultOwnerId,
  onSubmit,
  onClose,
}: ApiTokenFormModalProps) {
  const [name, setName] = useState('');
  const [ownerId, setOwnerId] = useState(defaultOwnerId);
  const [scopes, setScopes] = useState<readonly ApiScope[]>(READONLY_SCOPES);
  const [expiresInDays, setExpiresInDays] = useState('');

  const close = (): void => {
    setName('');
    setOwnerId(defaultOwnerId);
    setScopes(READONLY_SCOPES);
    setExpiresInDays('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Выпустить токен API"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={name.trim().length === 0 || scopes.length === 0 || ownerId.length === 0}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                scopes: [...scopes],
                expiresInDays: expiresInDays === '' ? null : Number(expiresInDays),
                ownerId,
              })
            }
          >
            Выпустить
          </Button>
        </>
      }
    >
      <div className={styles['form']}>
        <Input
          label="Название"
          value={name}
          required
          maxLength={100}
          placeholder="Jira sync"
          hint="Название видно в списке токенов и в аудите."
          onChange={(event) => setName(event.target.value)}
        />

        <Select
          label="Владелец"
          value={ownerId}
          placeholder="Выберите владельца"
          options={members.map((member) => ({
            value: member.user.id,
            label:
              member.user.kind === 'ai_agent'
                ? `${member.user.displayName} · AI агент`
                : member.user.displayName,
            description: `@${member.user.login}`,
          }))}
          onChange={setOwnerId}
        />

        <fieldset className={styles['scopeFieldset']}>
          <legend>Права</legend>
          {SCOPE_LABELS.map((item) => (
            <label key={item.value} className={styles['scopeOption']}>
              <input
                type="checkbox"
                checked={scopes.includes(item.value)}
                onChange={(event) =>
                  setScopes((current) =>
                    event.target.checked
                      ? [...current, item.value]
                      : current.filter((scope) => scope !== item.value),
                  )
                }
              />
              <span>
                {item.label} <span className="u-muted">· {item.group}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <Input
          label="Срок действия, дней"
          type="number"
          min={1}
          max={365}
          value={expiresInDays}
          placeholder="Бессрочно"
          hint="Пусто — токен без срока. Максимум 365 дней."
          onChange={(event) => setExpiresInDays(event.target.value)}
        />
      </div>
    </Modal>
  );
}
